import { css, cx } from "@emotion/css";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useCallback } from "react";
import * as React from "react";
import { useFocusFirstFocusableElement } from "../../hooks/useFocusTrap";
import { Overlay } from "./Overlay";
export interface DialogBaseProps {
    children?: React.ReactNode;
    open: boolean;
    onClose?: () => void;
    closeOnEscape?: boolean;
    closeOnBackdropClick?: boolean;
    accessibleName?: string;
    autoFocusFirstElement?: boolean;
}
export function DialogBase(props: DialogBaseProps) {
    const { children, open, onClose, closeOnEscape = true, closeOnBackdropClick = true, autoFocusFirstElement = true } = props;
    const focusFirstElement = useFocusFirstFocusableElement();
    const latestCloseRef = React.useRef(props.onClose);
    latestCloseRef.current = props.onClose;
    const dialogRef = React.useRef<HTMLDivElement>(null);
    const [isClosing, setIsClosing] = React.useState(false);
    const [shouldRender, setShouldRender] = React.useState(open);
    const hasStartedClosingRef = React.useRef(false);
    const handleClose = useCallback(() => {
        setIsClosing(true);
        hasStartedClosingRef.current = true;
    }, []);
    const handleKeyUp = useCallback((event: KeyboardEvent) => {
        if (closeOnEscape && event.key === "Escape" && event.target instanceof HTMLElement) {
            handleClose();
        }
        else {
            event.preventDefault();
        }
    }, [closeOnEscape, handleClose]);
    React.useEffect(() => {
        document.addEventListener("keyup", handleKeyUp);
        return () => {
            document.removeEventListener("keyup", handleKeyUp);
        };
    }, [handleKeyUp]);
    React.useEffect(() => {
        if (open) {
            setShouldRender(true);
            setIsClosing(false);
            hasStartedClosingRef.current = false;
        }
        else if (!hasStartedClosingRef.current) {
            setIsClosing(true);
            hasStartedClosingRef.current = true;
        }
    }, [open]);
    React.useEffect(() => {
        if (open && shouldRender && dialogRef.current) {
            if (autoFocusFirstElement) {
                focusFirstElement(dialogRef.current);
            }
            else {
                // Still focus the dialog container, ensuring the Overlay's focus trap works correctly
                dialogRef.current.focus();
            }
        }
    }, [focusFirstElement, open, shouldRender, autoFocusFirstElement]);
    React.useLayoutEffect(() => {
        if (!isClosing)
            return;
        const dialogElement = dialogRef.current;
        if (!dialogElement)
            return;
        const handleAnimationEnd = (event: AnimationEvent) => {
            if (event.animationName === "dialogFadeOut") {
                setShouldRender(false);
                hasStartedClosingRef.current = false;
                onClose?.();
            }
        };
        dialogElement.addEventListener("animationend", handleAnimationEnd);
        return () => {
            dialogElement.removeEventListener("animationend", handleAnimationEnd);
        };
    }, [isClosing, onClose]);
    return (<>
            {shouldRender && (<Overlay isClosing={isClosing} onClick={() => {
                if (closeOnBackdropClick) {
                    handleClose();
                }
            }}>
                    <div ref={dialogRef} className={cx(styles.dialog, {
                [styles.closing]: isClosing,
            })} role={"dialog"} aria-label={props.accessibleName} aria-modal={"true"} tabIndex={-1}>
                        {children}
                    </div>
                </Overlay>)}
        </>);
}
const styles = {
    dialog: css({
        border: "none",
        padding: 0,
        position: "fixed",
        margin: "auto",
        maxWidth: "100vw", // without this the browser enforces a small amount of horizontal margin
        maxHeight: "100vh", // without this the browser enforces a small amount of vertical margin
        borderRadius: borderRadius.large,
        background: themeTokens.color.dialog.background.primary,
        opacity: 0,
        animationName: "dialogSlideUpFadeIn",
        animationDuration: "200ms",
        animationTimingFunction: "ease-in",
        animationFillMode: "forwards",
        animationDelay: "20ms",
        "&:focus": {
            outline: "none",
        },
        "& *": {
            boxSizing: "border-box",
        },
        "::backdrop": {
            background: themeTokens.color.overlay.primary,
        },
        "@keyframes dialogSlideUpFadeIn": {
            from: {
                opacity: 0,
                transform: "translateY(10px)",
            },
            to: {
                opacity: 1,
                transform: "translateY(0)",
            },
        },
    }),
    closing: css({
        animation: "dialogFadeOut 200ms ease-out forwards",
        "@keyframes dialogFadeOut": {
            from: {
                opacity: 1,
                transform: "translateY(0)",
            },
            to: {
                opacity: 0,
                transform: "translateY(0)",
            },
        },
    }),
};
DialogBase.displayName = "DialogBase";
